// The order the chat feed reads in. Pure, so the comparator can be tested
// without mounting the panel.

import type { ChatMessageDto } from '@/ApiClient';
import { parseServerUtcDate } from '@lib/formatters';

export const timeOf = (message: ChatMessageDto): number => parseServerUtcDate(message.createdAt).getTime();

/**
 * Undated tasks sort last.
 *
 * A finite sentinel rather than Infinity: two undated tasks subtract to NaN,
 * and `NaN !== 0` is true, so the comparator would take the deadline branch and
 * the newest-first tiebreaker below it would never run.
 */
const NO_DEADLINE = Number.MAX_SAFE_INTEGER;

export const deadlineOf = (message: ChatMessageDto): number =>
  message.deadline ? parseServerUtcDate(message.deadline).getTime() : NO_DEADLINE;

/**
 * Deadline ascending, undated last, newest first among equals — the task views
 * only need an order that holds *within* a status group, since the feed buckets
 * by status afterwards. The conversation views read oldest → newest, the way
 * every chat client does, with the composer under them.
 */
export const compareChatMessages =
  (grouped: boolean) =>
  (a: ChatMessageDto, b: ChatMessageDto): number => {
    if (grouped) {
      const byDeadline = deadlineOf(a) - deadlineOf(b);
      if (byDeadline !== 0) return byDeadline;

      return timeOf(b) - timeOf(a);
    }
    return timeOf(a) - timeOf(b);
  };
